import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as notifApi from './notifications.api';

import type {
  CreateNotificationEventRequest,
  EmailLogListQuery,
  UpdateNotificationChannelConfigRequest,
  CreateNotificationAudienceRequest,
  CreateNotificationRuleRequest,
  CreateNotificationTemplateRequest,
  DryRunNotificationRuleRequest,
  MyNotificationsInboxQuery,
  NotificationAudienceListQuery,
  NotificationEventListQuery,
  NotificationListQuery,
  NotificationRuleListQuery,
  NotificationTemplateListQuery,
  PreviewNotificationTemplateRequest,
  PublishNotificationRequest,
  TestSendNotificationRequest,
  UpdateNotificationAudienceRequest,
  UpdateNotificationChannelRequest,
  UpdateNotificationEventRequest,
  UpdateNotificationRuleRequest,
  UpdateNotificationTemplateRequest,
} from '@utazzo/shared';

const QK = {
  events: (q?: NotificationEventListQuery) => ['notifications', 'events', q] as const,
  channels: () => ['notifications', 'channels'] as const,
  templates: (q?: NotificationTemplateListQuery) => ['notifications', 'templates', q] as const,
  audiences: (q?: NotificationAudienceListQuery) => ['notifications', 'audiences', q] as const,
  rules: (q?: NotificationRuleListQuery) => ['notifications', 'rules', q] as const,
  history: (q?: NotificationListQuery) => ['notifications', 'history', q] as const,
  inbox: (q?: MyNotificationsInboxQuery) => ['notifications', 'me', 'inbox', q] as const,
};

const ALL_NOTIF_KEY = ['notifications'] as const;
const STALE = 30_000;

// =============================================================================
// Events
// =============================================================================

export const useNotificationEvents = (query: NotificationEventListQuery) =>
  useQuery({
    queryKey: QK.events(query),
    queryFn: () => notifApi.listNotificationEvents(query),
    staleTime: STALE,
  });

export const useUpdateNotificationEvent = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateNotificationEventRequest }) =>
      notifApi.updateNotificationEvent(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', 'events'] });
      toast.success('Event updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update event'),
  });
};

export const useCreateNotificationEvent = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateNotificationEventRequest) =>
      notifApi.createNotificationEvent(payload),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['notifications', 'events'] });
      toast.success(`Event "${row.code}" registered`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to register event'),
  });
};

export const useDeleteNotificationEvent = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notifApi.deleteNotificationEvent(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', 'events'] });
      toast.success('Event removed');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete event'),
  });
};

// =============================================================================
// Channels
// =============================================================================

export const useNotificationChannels = () =>
  useQuery({
    queryKey: QK.channels(),
    queryFn: () => notifApi.listNotificationChannels(),
    staleTime: STALE,
  });

export const useUpdateNotificationChannel = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateNotificationChannelRequest }) =>
      notifApi.updateNotificationChannel(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.channels() });
      toast.success('Channel updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update channel'),
  });
};

export const useNotificationChannelConfig = (code: string | null) =>
  useQuery({
    queryKey: ['notifications', 'channels', code, 'config'],
    queryFn: () => notifApi.getNotificationChannelConfig(code as string),
    enabled: !!code,
    staleTime: 0,
  });

export const useUpdateNotificationChannelConfig = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      code,
      payload,
    }: {
      code: string;
      payload: UpdateNotificationChannelConfigRequest;
    }) => notifApi.updateNotificationChannelConfig(code, payload),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ['notifications', 'channels', vars.code, 'config'] });
      toast.success('Channel config saved');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to save config'),
  });
};

export const useVerifyNotificationChannel = () =>
  useMutation({
    mutationFn: (code: string) => notifApi.verifyNotificationChannel(code),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Verify failed'),
  });

// =============================================================================
// Email logs (legacy EmailDispatcher rows, read-only)
// =============================================================================

export const useNotificationEmailLogs = (query: EmailLogListQuery) =>
  useQuery({
    queryKey: ['notifications', 'email-logs', query],
    queryFn: () => notifApi.listNotificationEmailLogs(query),
    staleTime: 10_000,
  });

// =============================================================================
// Templates
// =============================================================================

export const useNotificationTemplates = (query: NotificationTemplateListQuery) =>
  useQuery({
    queryKey: QK.templates(query),
    queryFn: () => notifApi.listNotificationTemplates(query),
    staleTime: STALE,
  });

export const useCreateNotificationTemplate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateNotificationTemplateRequest) =>
      notifApi.createNotificationTemplate(payload),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['notifications', 'templates'] });
      toast.success(`Template "${row.name}" created`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to create template'),
  });
};

export const useUpdateNotificationTemplate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateNotificationTemplateRequest }) =>
      notifApi.updateNotificationTemplate(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', 'templates'] });
      toast.success('Template updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update template'),
  });
};

export const useDeleteNotificationTemplate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notifApi.deleteNotificationTemplate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', 'templates'] });
      toast.success('Template deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete template'),
  });
};

export const usePreviewNotificationTemplate = () =>
  useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: PreviewNotificationTemplateRequest }) =>
      notifApi.previewNotificationTemplate(id, payload),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Preview failed'),
  });

// =============================================================================
// Audiences
// =============================================================================

export const useNotificationAudiences = (query: NotificationAudienceListQuery) =>
  useQuery({
    queryKey: QK.audiences(query),
    queryFn: () => notifApi.listNotificationAudiences(query),
    staleTime: STALE,
  });

export const useCreateNotificationAudience = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateNotificationAudienceRequest) =>
      notifApi.createNotificationAudience(payload),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['notifications', 'audiences'] });
      toast.success(`Audience "${row.name}" created`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to create audience'),
  });
};

export const useUpdateNotificationAudience = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateNotificationAudienceRequest }) =>
      notifApi.updateNotificationAudience(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', 'audiences'] });
      toast.success('Audience updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update audience'),
  });
};

export const useDeleteNotificationAudience = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notifApi.deleteNotificationAudience(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', 'audiences'] });
      toast.success('Audience deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete audience'),
  });
};

// =============================================================================
// Rules
// =============================================================================

export const useNotificationRules = (query: NotificationRuleListQuery) =>
  useQuery({
    queryKey: QK.rules(query),
    queryFn: () => notifApi.listNotificationRules(query),
    staleTime: STALE,
  });

export const useCreateNotificationRule = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateNotificationRuleRequest) =>
      notifApi.createNotificationRule(payload),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['notifications', 'rules'] });
      toast.success(`Rule "${row.name}" created`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to create rule'),
  });
};

export const useUpdateNotificationRule = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateNotificationRuleRequest }) =>
      notifApi.updateNotificationRule(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', 'rules'] });
      toast.success('Rule updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update rule'),
  });
};

export const useDeleteNotificationRule = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notifApi.deleteNotificationRule(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', 'rules'] });
      toast.success('Rule deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete rule'),
  });
};

export const useDryRunNotificationRule = () =>
  useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: DryRunNotificationRuleRequest }) =>
      notifApi.dryRunNotificationRule(id, payload),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Dry-run failed'),
  });

// =============================================================================
// History
// =============================================================================

export const useNotificationHistory = (query: NotificationListQuery) =>
  useQuery({
    queryKey: QK.history(query),
    queryFn: () => notifApi.listNotificationHistory(query),
    staleTime: 10_000,
  });

export const useMyAllNotifications = (query: NotificationListQuery) =>
  useQuery({
    queryKey: ['notifications', 'me', 'all', query],
    queryFn: () => notifApi.listMyAllNotifications(query),
    staleTime: 10_000,
  });

export const useRetryNotification = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notifApi.retryNotification(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', 'history'] });
      qc.invalidateQueries({ queryKey: ['notifications', 'me', 'inbox'] });
      toast.success('Retry queued');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Retry failed'),
  });
};

// =============================================================================
// /me inbox (bell widget)
// =============================================================================

export const useMyNotificationsInbox = (
  query: MyNotificationsInboxQuery,
  options?: { refetchInterval?: number; enabled?: boolean },
) =>
  useQuery({
    queryKey: QK.inbox(query),
    queryFn: () => notifApi.getMyNotificationsInbox(query),
    // No websocket layer - the in-app channel is polling-based per
    // ADR-0048 §10.2.1. ~30s is the polite default; the caller can
    // tighten it for an opened dropdown / dashboard widget.
    refetchInterval: options?.refetchInterval ?? 30_000,
    refetchIntervalInBackground: false,
    enabled: options?.enabled,
    staleTime: 5_000,
  });

export const useMarkNotificationRead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notifApi.markNotificationRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', 'me', 'inbox'] });
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to mark read'),
  });
};

export const useMarkAllNotificationsRead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => notifApi.markAllNotificationsRead(),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['notifications', 'me', 'inbox'] });
      toast.success(`${res.markedRead} marked as read`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to mark all read'),
  });
};

// =============================================================================
// Escape hatches
// =============================================================================

export const usePublishNotificationEvent = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: PublishNotificationRequest) => notifApi.publishNotificationEvent(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ALL_NOTIF_KEY });
      toast.success('Event published');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Publish failed'),
  });
};

export const useTestSendNotification = () =>
  useMutation({
    mutationFn: (payload: TestSendNotificationRequest) => notifApi.testSendNotification(payload),
    onSuccess: () => toast.success('Test send dispatched'),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Test send failed'),
  });

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
